/**
 * Eventos que el núcleo emite a los módulos por webhook. Ver docs/FASE-0.md §5.
 * El payload lleva el estado *resultante*; el módulo hace upsert en su read-model.
 */
import { z } from "zod";
import { PATTERNS } from "./constants.js";
import { SubscriptionStatusSchema } from "./entitlements.js";
export const OrganizationStatusSchema = z.enum(["active", "suspended", "closed"]);
export const BrandingSchema = z.object({
    primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    logoUrl: z.url().optional(),
    initials: z.string().min(1).max(3).optional(),
});
/** Read-model público de una organización, tal como viaja en eventos y en el API. */
export const OrganizationSchema = z.object({
    id: z.uuid(),
    slug: z.string().regex(PATTERNS.ORG_SLUG),
    legalName: z.string(),
    displayName: z.string(),
    taxId: z.string().nullable(),
    country: z.string().length(2),
    timezone: z.string(),
    status: OrganizationStatusSchema,
    branding: BrandingSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const MembershipStatusSchema = z.enum(["active", "invited", "suspended", "removed"]);
/** Membresía de un usuario en una organización, con sus roles por módulo. */
export const MembershipSchema = z.object({
    userId: z.uuid(),
    orgId: z.uuid(),
    status: MembershipStatusSchema,
    isDefault: z.boolean(),
    user: z.object({
        email: z.email(),
        fullName: z.string(),
        status: z.enum(["active", "invited", "suspended"]),
    }),
    /** moduleCode → role codes. */
    roles: z.record(z.string().regex(PATTERNS.MODULE_CODE), z.array(z.string().regex(PATTERNS.KEY))),
    updatedAt: z.iso.datetime(),
});
export const SubscriptionSchema = z.object({
    id: z.uuid(),
    orgId: z.uuid(),
    moduleCode: z.string().regex(PATTERNS.MODULE_CODE),
    planId: z.uuid(),
    planCode: z.string(),
    status: SubscriptionStatusSchema,
    seats: z.number().int().nonnegative().nullable(),
    entitlementVersion: z.number().int().nonnegative(),
    trialEndsAt: z.iso.datetime().nullable(),
    currentPeriodStart: z.iso.datetime().nullable(),
    currentPeriodEnd: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
});
const envelope = (type, data) => z.object({
    id: z.uuid(),
    type: z.literal(type),
    occurredAt: z.iso.datetime(),
    orgId: z.uuid().nullable(),
    data,
});
export const WebhookEventSchema = z.discriminatedUnion("type", [
    envelope("organization.updated", z.object({ organization: OrganizationSchema })),
    envelope("organization.suspended", z.object({ organization: OrganizationSchema, reason: z.string().nullable() })),
    envelope("organization.reactivated", z.object({ organization: OrganizationSchema })),
    envelope("membership.changed", z.object({
        membership: MembershipSchema,
        /** Solo los módulos afectados por el cambio; el módulo receptor debe filtrar por su código. */
        affectedModules: z.array(z.string().regex(PATTERNS.MODULE_CODE)),
    })),
    envelope("subscription.changed", z.object({
        subscription: SubscriptionSchema,
        previousStatus: SubscriptionStatusSchema.nullable(),
        previousPlanCode: z.string().nullable(),
    })),
    envelope("user.updated", z.object({
        user: z.object({
            id: z.uuid(),
            email: z.email(),
            fullName: z.string(),
            status: z.enum(["active", "invited", "suspended"]),
            locale: z.string(),
        }),
        /** Organizaciones donde el usuario tiene membresía; el módulo actualiza solo las suyas. */
        orgIds: z.array(z.uuid()),
    })),
]);
//# sourceMappingURL=webhooks.js.map